import { useEffect, useState } from "react";
import clsx from "clsx";
import { useCatacombsStore } from "../store/useCatacombsStore";
import { useGameStore } from "../store/useGameStore";
import { FormattedNumber } from "../components/FormattedNumber";

const TABS = [
  { id: "fight" as const, label: "Explore" },
  { id: "auto" as const, label: "Auto-clear" },
  { id: "shop" as const, label: "Shop" },
];

export function CatacombsScreen() {
  const { hero, balance, setScreen } = useGameStore();
  const {
    state,
    shop,
    tab,
    loading,
    fighting,
    actionLoading,
    error,
    setTab,
    refresh,
    refreshShop,
    fight,
    autoClearStart,
    autoClearClaim,
    autoClearSkip,
    buyItem,
  } = useCatacombsStore();

  const [timerLeft, setTimerLeft] = useState(0);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (tab === "shop") void refreshShop();
  }, [tab, refreshShop]);

  useEffect(() => {
    setTimerLeft(state?.auto_clear.seconds_remaining ?? 0);
  }, [state?.auto_clear.seconds_remaining]);

  useEffect(() => {
    if (timerLeft <= 0) return;
    const tick = setInterval(() => setTimerLeft((p) => Math.max(0, p - 1)), 1000);
    return () => clearInterval(tick);
  }, [timerLeft > 0]);

  const unlocked = state?.unlocked ?? (hero?.level ?? 0) >= (state?.unlock_level ?? 23);
  const crypto = state?.crypto ?? balance?.crypto ?? 0;

  return (
    <div className="p-4 space-y-4 pb-8">
      <button type="button" onClick={() => setScreen("main")} className="text-sm text-gray-400 hover:text-white">
        ← Back to Home
      </button>

      <div className="text-center space-y-1">
        <h2 className="text-xl font-bold font-game text-gold-400">🕳️ Abandoned Catacombs</h2>
        <p className="text-gray-400 text-sm">
          Crypto: <span className="text-cyan-300 font-bold"><FormattedNumber value={crypto} /></span>
        </p>
      </div>

      {!unlocked && (
        <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 text-center">
          <p className="text-gray-300 text-sm">Unlocks at hero level {state?.unlock_level ?? 23}</p>
        </div>
      )}

      {unlocked && (
        <>
          <div className="flex gap-1 bg-kingdom-dark rounded-xl p-1 border border-kingdom-mid">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={clsx(
                  "flex-1 py-2 rounded-lg text-xs font-bold transition-colors",
                  tab === t.id ? "bg-kingdom-glow text-black" : "text-gray-400 hover:text-white",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">{error}</div>
          )}

          {loading && !state && <p className="text-gray-500 text-sm text-center py-6">Loading...</p>}

          {state && tab === "fight" && (
            <FightPanel state={state} fighting={fighting} onFight={() => void fight()} />
          )}

          {state && tab === "auto" && (
            <AutoClearPanel
              state={state}
              timerLeft={timerLeft}
              actionLoading={actionLoading}
              diamonds={balance?.diamonds ?? 0}
              onStart={() => void autoClearStart()}
              onClaim={() => void autoClearClaim()}
              onSkip={() => void autoClearSkip()}
            />
          )}

          {tab === "shop" && shop && (
            <ShopPanel
              shop={shop}
              actionLoading={actionLoading}
              onBuy={(code) => void buyItem(code)}
            />
          )}
        </>
      )}
    </div>
  );
}

function FightPanel({
  state,
  fighting,
  onFight,
}: {
  state: NonNullable<ReturnType<typeof useCatacombsStore.getState>["state"]>;
  fighting: boolean;
  onFight: () => void;
}) {
  const preview = state.next_preview;
  const cleared = state.highest_cleared;

  return (
    <div className="space-y-4">
      <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-3">
        <div className="flex justify-between text-xs text-gray-400 mb-2">
          <span>Floor {cleared}/{state.max_floor}</span>
          {state.cleared_all && <span className="text-gold-400">All cleared!</span>}
        </div>
        <div className="flex gap-0.5 h-2 rounded-full overflow-hidden bg-kingdom-dark">
          {Array.from({ length: Math.min(state.max_floor, 100) }).map((_, i) => (
            <div
              key={i}
              className={clsx(
                "flex-1 min-w-[2px]",
                i < cleared
                  ? state.next_preview && i + 1 === state.next_preview.floor && state.next_preview.is_boss
                    ? "bg-amber-500"
                    : "bg-emerald-600"
                  : "bg-gray-800",
              )}
            />
          ))}
        </div>
      </div>

      {preview && (
        <div className="bg-kingdom-dark/80 border border-kingdom-mid rounded-xl p-4 space-y-3">
          <div className="flex justify-between items-center">
            <div>
              <div className="font-bold text-white">
                Floor {preview.floor} {preview.is_boss ? "· Boss" : `· ${preview.enemy_count} skeletons`}
              </div>
              <div className="text-xs text-cyan-300">+{preview.crypto_reward} Crypto</div>
            </div>
          </div>
          <div className="space-y-1">
            {preview.enemies.map((e, i) => (
              <div key={i} className="flex justify-between text-sm text-gray-300">
                <span>{e.name}</span>
                <span className="text-gray-500 tabular-nums">HP {e.hp.toLocaleString()}</span>
              </div>
            ))}
          </div>
          <button
            type="button"
            disabled={fighting || state.cleared_all}
            onClick={onFight}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-rose-600 to-red-700 text-white font-bold disabled:opacity-50 active:scale-95"
          >
            {fighting ? "Fighting..." : state.cleared_all ? "Complete" : "⚔️ Fight"}
          </button>
          <p className="text-[10px] text-gray-500 text-center">No energy cost</p>
        </div>
      )}
    </div>
  );
}

function AutoClearPanel({
  state,
  timerLeft,
  actionLoading,
  diamonds,
  onStart,
  onClaim,
  onSkip,
}: {
  state: NonNullable<ReturnType<typeof useCatacombsStore.getState>["state"]>;
  timerLeft: number;
  actionLoading: boolean;
  diamonds: number;
  onStart: () => void;
  onClaim: () => void;
  onSkip: () => void;
}) {
  const ac = state.auto_clear;
  const skipCost = ac.skip_diamond_cost;
  const vipSkip = ac.vip_skip_free ?? false;

  return (
    <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-4">
      <p className="text-gray-300 text-sm">
        Once per day, auto-clear all floors up to your highest cleared ({state.highest_cleared}) for combined rewards.
      </p>

      {state.highest_cleared === 0 && (
        <p className="text-gray-500 text-sm text-center">Clear at least one floor manually first.</p>
      )}

      {state.highest_cleared > 0 && ac.available_today && !ac.in_progress && (
        <button
          type="button"
          disabled={actionLoading}
          onClick={onStart}
          className="w-full py-3 rounded-xl bg-kingdom-glow text-black font-bold disabled:opacity-50"
        >
          Start auto-clear ({Math.round(ac.duration_seconds / 60)} min)
        </button>
      )}

      {ac.in_progress && !ac.ready_to_claim && (
        <div className="text-center space-y-3">
          <p className="text-amber-300 font-semibold tabular-nums">{formatTimer(timerLeft)}</p>
          <p className="text-gray-400 text-xs">Collect when the timer ends</p>
          <button
            type="button"
            disabled={actionLoading || (!vipSkip && diamonds < skipCost)}
            onClick={onSkip}
            className="w-full py-2.5 rounded-xl border border-kingdom-glow text-kingdom-glow font-bold text-sm disabled:opacity-40"
          >
            {vipSkip ? "Skip free (VIP)" : `Skip for 💎${skipCost}`}
          </button>
          {!vipSkip && skipCost > 0 && (
            <p className="text-[10px] text-gray-500 text-center">Free with VIP at higher tiers</p>
          )}
        </div>
      )}

      {ac.ready_to_claim && (
        <button
          type="button"
          disabled={actionLoading}
          onClick={onClaim}
          className="w-full py-3 rounded-xl bg-emerald-600 text-white font-bold disabled:opacity-50"
        >
          Claim rewards
        </button>
      )}

      {!ac.available_today && !ac.in_progress && state.highest_cleared > 0 && (
        <p className="text-gray-500 text-sm text-center">Already used today. Come back tomorrow.</p>
      )}
    </div>
  );
}

function ShopPanel({
  shop,
  actionLoading,
  onBuy,
}: {
  shop: NonNullable<ReturnType<typeof useCatacombsStore.getState>["shop"]>;
  actionLoading: boolean;
  onBuy: (code: string) => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-400 text-center">
        Balance: <span className="text-cyan-300 font-bold"><FormattedNumber value={shop.crypto} /></span> Crypto
      </p>
      {shop.items.map((item) => (
        <div key={item.code} className="bg-kingdom-dark/80 border border-kingdom-mid rounded-xl p-3 flex gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-bold text-white text-sm">{item.name}</div>
            <div className="text-xs text-gray-400 mt-0.5">{item.description}</div>
            <div className="text-xs text-cyan-300 mt-1">{item.crypto_cost} Crypto</div>
          </div>
          <button
            type="button"
            disabled={actionLoading || shop.crypto < item.crypto_cost}
            onClick={() => onBuy(item.code)}
            className="self-center px-4 py-2 rounded-lg bg-kingdom-glow text-black font-bold text-sm disabled:opacity-40"
          >
            Buy
          </button>
        </div>
      ))}
    </div>
  );
}

function formatTimer(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
